import React, { createContext, useContext, useState, useCallback } from 'react';

const MiniPlayerContext = createContext(null);

export const useMiniPlayer = () => {
  const context = useContext(MiniPlayerContext);
  if (!context) {
    throw new Error('useMiniPlayer must be used within a MiniPlayerProvider');
  }
  return context;
};

export const MiniPlayerProvider = ({ children }) => {
  const [miniPlayerVideo, setMiniPlayerVideo] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false); // true = fullscreen, false = mini
  const [videoQueue, setVideoQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Start playing a video (opens in expanded/fullscreen mode)
  const playVideo = useCallback((video, queue = [], index = 0) => {
    setMiniPlayerVideo(video);
    setVideoQueue(queue);
    setCurrentIndex(index);
    setIsExpanded(true); // Start in fullscreen mode
  }, []);

  // Minimize to PiP mode (video keeps playing)
  const minimizePlayer = useCallback(() => {
    setIsExpanded(false);
  }, []);

  // Expand to fullscreen mode (video keeps playing)
  const expandPlayer = useCallback(() => {
    setIsExpanded(true);
  }, []);

  // Close player completely
  const closeMiniPlayer = useCallback(() => {
    setMiniPlayerVideo(null);
    setVideoQueue([]);
    setCurrentIndex(0);
    setIsExpanded(false);
  }, []);

  // Play next video in queue
  const playNext = useCallback(() => {
    if (videoQueue.length === 0) return;
    const nextIndex = (currentIndex + 1) % videoQueue.length;
    setCurrentIndex(nextIndex);
    setMiniPlayerVideo(videoQueue[nextIndex]);
  }, [videoQueue, currentIndex]);

  // Play previous video in queue
  const playPrevious = useCallback(() => {
    if (videoQueue.length === 0) return;
    const prevIndex = currentIndex <= 0 ? videoQueue.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setMiniPlayerVideo(videoQueue[prevIndex]);
  }, [videoQueue, currentIndex]);

  // Legacy function name for compatibility
  const minimizeVideo = useCallback((video, queue = [], index = 0, startTime = 0) => {
    setMiniPlayerVideo(video);
    setVideoQueue(queue);
    setCurrentIndex(index);
    setIsExpanded(true); // Open in expanded/fullscreen mode by default
  }, []);
  
  // Compute isMinimized for backward compatibility (inverse of isExpanded)
  const isMinimized = !isExpanded;
  
  // Alias for expandPlayer
  const expandVideo = expandPlayer;

  const value = {
    miniPlayerVideo,
    isExpanded,
    isMinimized, // Legacy compatibility (inverse of isExpanded)
    videoQueue,
    currentIndex,
    playVideo,
    minimizePlayer,
    expandPlayer,
    expandVideo, // Legacy alias
    closeMiniPlayer,
    playNext,
    playPrevious,
    minimizeVideo, // Legacy compatibility - opens in expanded mode
    currentTime: 0, // Placeholder for compatibility
  };

  return (
    <MiniPlayerContext.Provider value={value}>
      {children}
    </MiniPlayerContext.Provider>
  );
};

export default MiniPlayerContext;
